import { createActions, createReducer } from "reduxsauce"
import io from "socket.io-client"
import { eventChannel } from "redux-saga"
import { get, some, keyBy } from "lodash"
import { call, put, take, race, delay, takeLatest, select } from "redux-saga/effects"

export const { Types, Creators } = createActions({
  init: [],
  toggle: [],
  open: [],
  update: ["notification"],
  updateSuccess: ["state"],
}, {
  prefix: "ompnt/modules/server-notifications/",
})

const DEFAULT_STATE = {
  open: false,
  updatedNotifications: {},
  potentialNewNotifications: {},
}

function handleToggle(state) {
  return { ...state, open: !state.open, potentialNewNotifications: {} }
}

function handleOpen(state) {
  return { ...state, open: true, potentialNewNotifications: {} }
}

function handleUpdateSuccess(state, { state: { updatedNotifications, potentialNewNotifications } }) {
  return {
    ...state,
    updatedNotifications: { ...state.updatedNotifications, ...updatedNotifications },
    potentialNewNotifications,
  }
}

export default createReducer(DEFAULT_STATE, {
  [Types.TOGGLE]: handleToggle,
  [Types.UPDATE_SUCCESS]: handleUpdateSuccess,
  [Types.OPEN]: handleOpen,
})

function* handleUpdate({ notification }) {
  const { serverNotifications, startingNotifications } = yield select(state => ({
    serverNotifications: state.modules.serverNotifications,
    startingNotifications: state.global.data.notifications.data,
  }))

  const currentErrorCount = get(serverNotifications, ["updatedNotifications", notification.id, "error"], -1)
  const currentSuccessCount = get(serverNotifications, ["updatedNotifications", notification.id, "success"], -1)
  const newErrorCount = get(notification, "error", 0)
  const newSuccessCount = get(notification, "success", 0)

  // If we have aleardy a notification with a higher count, ignore the incoming notification
  if (currentErrorCount + currentSuccessCount >= newErrorCount + newSuccessCount) {
    yield put(Creators.updateSuccess(serverNotifications))
  }

  let potentialNewNotifications = serverNotifications.potentialNewNotifications
  // if this is an update on a notification we didn't receive an update for, it might be a new notification
  // we'll have to compare it with the old ones to make sure
  if (!serverNotifications.updatedNotifications[notification.id] && !serverNotifications.potentialNewNotifications[notification.id]) {
    potentialNewNotifications = { ...serverNotifications.potentialNewNotifications, [notification.id]: true }
  }

  const startingNotificationsMap = keyBy(startingNotifications, "id")

  if (some(potentialNewNotifications, (_, key) => !startingNotificationsMap[key])) {
    yield put(Creators.open())
  }

  yield put(Creators.updateSuccess({
    updatedNotifications: {
      [notification.id]: notification,
    },
    potentialNewNotifications,
  }))
}

export function* initNotification() {
  try {
    const { accountId } = yield select(state => ({
      accountId: get(state, "global.selected.accountId"),
    }))

    yield takeLatest(Types.UPDATE, handleUpdate)

    const socket = yield call(io, {
      query: {
        account: accountId,
      },
      transports: ["websocket", "polling"],
    })

    // then create a socket channel
    const socketChannel = eventChannel(emitter => {
      socket.on("notification", emitter)
      return () => {
        socket.off("notification")
      }
    })

    let lastAction
    let lastTime = Date.now()
    let countDown = 0 // handle leading action

    // This is to throttle the notifications
    while (true) {
      // Wait for the first of an event, or a delay.
      const racers = [take(socketChannel)]
      // Don't wait for a delay if it expired without an event last time
      if (countDown) {
        racers.push(delay(countDown))
      }
      const winner = yield race(racers)

      // Reduce the delay
      const now = Date.now()
      countDown -= (now - lastTime)
      lastTime = now

      // If we got triggered by an action, keep it in memory
      if (winner[0]) {
        lastAction = winner[0]
      }

      // If the delay expired
      if (countDown <= 0) {
        // If we have an action, executy it, then restart the delay
        if (lastAction) {
          yield put(Creators.update(lastAction))
          lastAction = null
          countDown = 200
          // If not, restart the machine
        } else {
          countDown = 0
        }
      }
    }
  } catch (err) {
    console.log(err)
  }
}

export function* saga() {
  yield takeLatest(Types.INIT, initNotification)
}
